import { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Paper,
  Container,
  Typography,
  IconButton,
  Box,
  Divider,
  TextField,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import { INITIAL_COMP_OFF_FORM_STATE } from '../constants';
import CompOffRequestSchema from '../schema/compOffSchema';
import { getReviewers } from '../../../services/compOffService';

const CompOffRequestForm = ({ open, onClose, onSubmit, compOffData = null, isEdit = false }) => {
  const { data: reviewersResponse } = useQuery({
    queryKey: ['compOffReviewers'],
    queryFn: async () => {
      try {
        const response = await getReviewers();
        return { reviewers: response?.data?.data || [] };
      } catch (error) {
        console.error('Error fetching comp-off reviewers:', error);
        return { reviewers: [] };
      }
    },
    enabled: open,
  });

  const reviewersList = reviewersResponse?.reviewers || [];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const submitData = {
        ...values,
        workDate: values.workDate ? dayjs(values.workDate).format('YYYY-MM-DD') : null,
        hoursWorked: parseFloat(values.hoursWorked),
        reviewers: values.reviewers && values.reviewers.length > 0 ? values.reviewers : undefined,
      };
      await onSubmit(submitData);
      if (!isEdit) {
        resetForm({ values: INITIAL_COMP_OFF_FORM_STATE });
      }
    } catch (error) {
      // Error handled by mutation's onError
    } finally {
      setSubmitting(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit && compOffData) {
      return {
        workDate: compOffData.workDate ? dayjs(compOffData.workDate) : null,
        hoursWorked: compOffData.hoursWorked || '',
        tasksCompleted: compOffData.tasksCompleted || '',
        description: compOffData.description || '',
        reviewers: compOffData.reviewers || [],
      };
    }
    return INITIAL_COMP_OFF_FORM_STATE;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: CompOffRequestSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const { values, errors, touched, isSubmitting, submitForm, getFieldProps, setFieldValue, resetForm } = formik;

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_COMP_OFF_FORM_STATE });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? 'Edit Comp-Off Request' : 'Request Comp-Off'}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Container maxWidth="sm">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Stack spacing={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Work Date (Weekend / Holiday)"
                      value={values.workDate ? dayjs(values.workDate) : null}
                      onChange={(newValue) => setFieldValue('workDate', newValue)}
                      inputFormat="DD-MMM-YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.workDate && Boolean(errors.workDate)}
                          helperText={
                            touched.workDate && errors.workDate
                              ? errors.workDate
                              : 'Select the weekend or holiday you worked on'
                          }
                          fullWidth
                          required
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <TextField
                    label="Hours Worked"
                    {...getFieldProps('hoursWorked')}
                    type="number"
                    inputProps={{ min: 4, max: 24, step: 0.5 }}
                    error={touched.hoursWorked && Boolean(errors.hoursWorked)}
                    helperText={touched.hoursWorked ? errors.hoursWorked : 'Enter total hours worked (e.g. 4, 8)'}
                    fullWidth
                    required
                  />

                  <TextField
                    label="Tasks Completed"
                    {...getFieldProps('tasksCompleted')}
                    inputProps={{ maxLength: 1000 }}
                    error={touched.tasksCompleted && Boolean(errors.tasksCompleted)}
                    helperText={
                      touched.tasksCompleted
                        ? errors.tasksCompleted
                        : `${values.tasksCompleted?.length || 0}/1000 characters`
                    }
                    multiline
                    rows={3}
                    fullWidth
                    required
                  />

                  <TextField
                    label="Description"
                    {...getFieldProps('description')}
                    inputProps={{ maxLength: 1000 }}
                    error={touched.description && Boolean(errors.description)}
                    helperText={
                      touched.description
                        ? errors.description
                        : `${values.description?.length || 0}/1000 characters`
                    }
                    multiline
                    rows={3}
                    fullWidth
                    required
                  />

                  <FormControl fullWidth error={touched.reviewers && Boolean(errors.reviewers)} required>
                    <InputLabel>Select Reviewers (Managers/HR) *</InputLabel>
                    <Select
                      multiple
                      value={values.reviewers || []}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('reviewers', typeof value === 'string' ? value.split(',') : value);
                      }}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return '';
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((reviewerId) => {
                              const reviewer = reviewersList.find((r) => r.id === reviewerId);
                              return (
                                <Chip
                                  key={reviewerId}
                                  label={reviewer ? reviewer.userName : reviewerId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                      label="Select Reviewers (Managers/HR) *"
                    >
                      {reviewersList.map((reviewer) => (
                        <MenuItem key={reviewer.id} value={reviewer.id}>
                          {reviewer.userName} (
                          {reviewer.userRoleId === 2 ? 'Manager' : reviewer.userRoleId === 6 ? 'HR' : 'Admin'})
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.reviewers && errors.reviewers && (
                      <FormHelperText>{errors.reviewers}</FormHelperText>
                    )}
                    {!errors.reviewers && (
                      <FormHelperText>
                        Select at least one reviewer. Admin and HR will automatically be included.
                      </FormHelperText>
                    )}
                  </FormControl>

                  <LoadingButton
                    fullWidth
                    size="large"
                    onClick={submitForm}
                    variant="contained"
                    loading={isSubmitting}
                  >
                    {isEdit ? 'Update Request' : 'Submit Request'}
                  </LoadingButton>
                </Stack>
              </Form>
            </FormikProvider>
          </Container>
        </Paper>
      </Box>
    </Modal>
  );
};

CompOffRequestForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  compOffData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default CompOffRequestForm;
